import { useMutation, useQueryClient } from '@tanstack/react-query';

import { putToPresignedTarget, readImageGeometry } from '@features/content/_shared/upload';
import { api } from '@shared/api/client';
import { asRows } from '@shared/api/envelope';
import { waitForDerivation } from '@shared/assets/derivation';

import { PORTRAIT_PURPOSE, PORTRAIT_SUBJECT_TYPE } from './constants';
import { creditProfileKey, creditProfilesScope } from './queries';
import type { CreditProfileRecord } from './types';

/**
 * Every write the two credited-person surfaces make.
 *
 * **No delete for a person.** The service publishes
 * `DELETE /admin/creditProfiles/{profileId}`, and this console does not call
 * it: catalog §20.11 names create, edit, and maintain — no destructive verb —
 * and §20.14's confirm-before-destruction rule enumerates taxonomy, shelf, and
 * plan management by name. Drawing the action here would be inventing it and
 * then inventing its confirmation. The route existing is not the same as the
 * surface being granted.
 */

interface CreateProfileBody {
  display_name: string;
  biography: string | null;
}

interface UpdateProfileBody {
  display_name?: string;
  biography?: string | null;
}

export function useCreateCreditProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateProfileBody) =>
      api.post<CreditProfileRecord>('content', '/admin/creditProfiles', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: creditProfilesScope() });
    },
  });
}

/**
 * The two public fields, and only the two. `PATCH` is `exclude_unset` on the
 * service side, so an untouched field is absent from the body rather than sent
 * back as the value that was read.
 */
export function useUpdateCreditProfile(slug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ profileId, body }: { profileId: string; body: UpdateProfileBody }) =>
      api.patch<CreditProfileRecord>('content', `/admin/creditProfiles/${profileId}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: creditProfileKey(slug) });
      // A rename moves the name the register prints, so the list is stale too.
      void queryClient.invalidateQueries({ queryKey: creditProfilesScope() });
    },
  });
}

interface ArtworkRegistration {
  artworkId: string;
  uploadTarget: string;
}

async function readPortraitUrl(profileId: string): Promise<string | null> {
  const rows = asRows<{ purpose: string; url: string }>(
    await api.get(
      'assets',
      `/artworks?subjectType=${PORTRAIT_SUBJECT_TYPE}&subjectId=${encodeURIComponent(profileId)}`,
    ),
  );

  return rows.find((row) => row.purpose === PORTRAIT_PURPOSE)?.url ?? null;
}

/**
 * The read, given the few seconds the derivation actually takes.
 *
 * `:completeUpload` returns as soon as the crop is **queued**, so a single
 * immediate read loses the race almost every time. The editorial thumbnail
 * learned this on develop: the objects landed in the bucket, the write-back
 * observed nothing, wrote nothing, and the post kept reporting `null`. The
 * bounded loop is shared with every other artwork upload in this console.
 */
async function waitForPortraitUrl(profileId: string, previous: string | null): Promise<string | null> {
  return waitForDerivation(
    () => readPortraitUrl(profileId),
    (url) => url !== null && url !== previous,
  );
}

/**
 * The portrait, in the three steps the `assets` contract has, plus the join.
 *
 * Register the object and receive a presigned target, PUT the bytes straight at
 * storage, then commit — the same sequence an editorial post's thumbnail uses,
 * against the same routes, with `CREDIT_PROFILE` as the subject.
 *
 * **`assets` does not write this back, and nothing else was going to.** A title's
 * cover is `content`'s own column, filled by the owning service when the crop
 * lands; `content__credit_profiles.portrait_url` is a column this console
 * PATCHes. Without the join the whole chain answers `202 → 200 → 202`, the object
 * sits in the bucket, and the person's page keeps drawing its neutral face.
 *
 * The subject id is the **credit profile's** ULID. Most credited people hold no
 * account at all, so this is never the account avatar of `backend-auth`.
 */
export function useUploadPortrait(slug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ profileId, file }: { profileId: string; file: File }) => {
      const geometry = await readImageGeometry(file);

      // The portrait this person already holds, so a REPLACEMENT waits for the
      // new key instead of resolving on the old one.
      const previous = await readPortraitUrl(profileId);

      const registration = await api.post<ArtworkRegistration>('assets', '/artworks', {
        subjectType: PORTRAIT_SUBJECT_TYPE,
        subjectId: profileId,
        declaredContentType: file.type,
        declaredSizeBytes: file.size,
        widthPx: geometry.widthPx,
        heightPx: geometry.heightPx,
      });

      await putToPresignedTarget(registration.uploadTarget, file);
      await api.post<null>('assets', `/artworks/${registration.artworkId}:completeUpload`);

      const portraitUrl = await waitForPortraitUrl(profileId, previous);
      if (portraitUrl !== null) {
        await api.patch<CreditProfileRecord>('content', `/admin/creditProfiles/${profileId}`, {
          portrait_url: portraitUrl,
        });
      }

      return portraitUrl;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: creditProfileKey(slug) });
      void queryClient.invalidateQueries({ queryKey: creditProfilesScope() });
    },
    // A half-finished transfer must not be replayed silently against a new
    // presigned target.
    retry: false,
  });
}

/** Clear the portrait without touching the stored object — the column is the surface. */
export function useClearPortrait(slug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ profileId }: { profileId: string }) =>
      api.patch<CreditProfileRecord>('content', `/admin/creditProfiles/${profileId}`, {
        portrait_url: null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: creditProfileKey(slug) });
      void queryClient.invalidateQueries({ queryKey: creditProfilesScope() });
    },
  });
}
